import _ from 'lodash'
import {DAL, pointerUtils, type DocumentManager} from '@wix/document-manager-core'
import type {PageMigrator} from '../dataMigrationRunner'
import {runOnPageComponents} from './utils/migrationUtil'
import * as constants from '../../../../constants/constants'
import type {CompRef, Pointer, Pointers} from '@wix/document-services-types'
import type {ComponentDefinitionExtensionAPI} from '../../../componentDefinition'
import {getRefComponentChildrenWithoutCorrespondingSlots} from '../../../../utils/slotsUtils'
import type {StructureExtensionAPI} from '../../../structure'

const name = 'removeRefComponentChildrenWithoutCorrespondingSlots'
const version = 2

const getChildrenPointer = (comp: Pointer) => pointerUtils.getInnerPointer(comp, 'components')

const deleteCompAndChildren = (dal: DAL, pointers: Pointers, rootComp: Pointer): void => {
    const children: Pointer[] = pointers.structure.getChildrenRecursively(rootComp)

    for (const childPointer of children) {
        dal.remove(childPointer)
    }
    dal.remove(rootComp)
}

const removeChildrenFromParent = (dal: DAL, parentPointer: Pointer, childrenToRemove: Pointer[]): void => {
    const childrenPointer: Pointer = getChildrenPointer(parentPointer)

    const allChildrenIds: string[] = dal.get(childrenPointer)
    const childrenToRemoveIds: string[] = childrenToRemove.map(p => p.id)
    const remainingChildrenIds: string[] = _.difference(allChildrenIds, childrenToRemoveIds)

    dal.set(childrenPointer, remainingChildrenIds)
}

const removeInvalidChildren = (
    {dal, pointers, logger}: DocumentManager,
    refCompPointer: Pointer,
    invalidChildren: CompRef[]
): void => {
    logger.interactionStarted(name, {compPointer: refCompPointer, invalidChildren})

    // we cannot use components.removeComponent since it currently does not support
    // the case of removing components from non desktop view mode,
    // so we need to remove them and all their nested children manually
    for (const child of invalidChildren) {
        deleteCompAndChildren(dal, pointers, child)
    }

    // remove the invalid children from the parent.components list
    removeChildrenFromParent(dal, refCompPointer, invalidChildren)

    logger.interactionEnded(name)
}

const fixRefComponentChildrenWithoutSlots = (
    documentManager: DocumentManager,
    pageId: string,
    compPointer: Pointer
) => {
    const {dal, pointers, extensionAPI} = documentManager
    const {componentDefinition, components} = extensionAPI as ComponentDefinitionExtensionAPI & StructureExtensionAPI
    const componentType: string = components.getComponentType(compPointer)

    if (!componentDefinition.isRefComponent(componentType)) return

    const invalidChildren: CompRef[] = getRefComponentChildrenWithoutCorrespondingSlots(
        dal,
        pointers,
        compPointer as CompRef
    )

    if (invalidChildren.length === 0) return

    removeInvalidChildren(documentManager, compPointer, invalidChildren)
}

const migratePage = (documentManager: DocumentManager, pageId: string) => {
    _.forEach(constants.VIEW_MODES, viewMode => {
        runOnPageComponents(documentManager, pageId, viewMode, fixRefComponentChildrenWithoutSlots)
    })
}

export const removeRefComponentChildrenWithoutCorrespondingSlots: PageMigrator = {
    migratePage,
    name,
    version
}
